import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, CheckCircle2, ExternalLink, KeyRound, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { getAddress, isAddress, zeroAddress } from 'viem'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Page, PageHeader } from '@/components/shell'
import { ContractWalletBar, sameAddress, useContractWallet } from '@/features/contracts/wallet-bar'
import { api, ApiError, type SystemFactoryStatus, type TaxExecutorResult } from '@/lib/api'
import {
  contractWalletErrorMessage,
  sendTaxExecutorOperation,
  waitForContractOperation,
} from '@/lib/contract-wallet'
import { explorerAddressUrl, explorerTxUrl, shortAddress } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/dashboard/contracts/dividends')({
  component: SystemVaultSettingsPage,
})

const TAX_EXECUTOR_PENDING_STORAGE_KEY = 'contract-tax-executor-audit-pending'

type TaxExecutorAuditPayload = { taxExecutor: string; reason: string; txHash: string }

function readTaxExecutorPending(): TaxExecutorAuditPayload | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(TAX_EXECUTOR_PENDING_STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as TaxExecutorAuditPayload
    if (
      !isAddress(value.taxExecutor) ||
      getAddress(value.taxExecutor) === zeroAddress ||
      typeof value.reason !== 'string' ||
      value.reason.trim().length < 10 ||
      value.reason.trim().length > 200 ||
      !/^0x[0-9a-fA-F]{64}$/.test(value.txHash)
    ) {
      throw new Error('invalid pending tax executor operation')
    }
    return { ...value, taxExecutor: getAddress(value.taxExecutor) }
  } catch {
    try {
      window.sessionStorage.removeItem(TAX_EXECUTOR_PENDING_STORAGE_KEY)
    } catch {
      // 存储不可用时只保留本标签页内存锁。
    }
    return null
  }
}

function storeTaxExecutorPending(value: TaxExecutorAuditPayload | null): void {
  try {
    if (value) {
      window.sessionStorage.setItem(TAX_EXECUTOR_PENDING_STORAGE_KEY, JSON.stringify(value))
    } else {
      window.sessionStorage.removeItem(TAX_EXECUTOR_PENDING_STORAGE_KEY)
    }
  } catch {
    // 交易已经提交时绝不因 sessionStorage 失败而主动解锁。
  }
}

class SubmittedTaxExecutorError extends Error {
  constructor(
    readonly pending: TaxExecutorAuditPayload,
    cause: unknown,
  ) {
    super(contractWalletErrorMessage(cause))
  }
}

class TaxExecutorAuditPendingError extends Error {
  readonly terminal: boolean

  constructor(
    readonly pending: TaxExecutorAuditPayload,
    cause: unknown,
  ) {
    super(cause instanceof ApiError ? cause.message : '审计接口暂时不可用。')
    this.terminal = cause instanceof ApiError && cause.errorCode === 'transaction_failed'
  }
}

class RevertedTransactionError extends Error {}

function SystemVaultSettingsPage() {
  const can = useCan()
  const canManage = can('contract', 'manage')
  const qc = useQueryClient()
  const { wallet, walletBusy, connect, switchChain, disconnect } = useContractWallet()
  const [taxExecutorInput, setTaxExecutorInput] = useState('')
  const [taxExecutorReason, setTaxExecutorReason] = useState('')
  const [taxExecutorPending, setTaxExecutorPending] = useState<TaxExecutorAuditPayload | null>(() =>
    readTaxExecutorPending(),
  )
  const [lastTaxExecutorResult, setLastTaxExecutorResult] = useState<TaxExecutorResult | null>(null)

  const status = useQuery({
    queryKey: ['contract-system-factory-status'],
    queryFn: () => api.get<SystemFactoryStatus>('/contracts/dividends/status'),
    staleTime: 15_000,
    retry: false,
  })

  useEffect(() => {
    if (status.data && taxExecutorInput === '') setTaxExecutorInput(status.data.taxExecutor)
  }, [status.data, taxExecutorInput])

  const normalizedTaxExecutor = isAddress(taxExecutorInput) ? getAddress(taxExecutorInput) : null
  const validTaxExecutor = normalizedTaxExecutor !== null && normalizedTaxExecutor !== zeroAddress
  const taxExecutorChanged =
    validTaxExecutor && !sameAddress(normalizedTaxExecutor, status.data?.taxExecutor)
  const validTaxExecutorReason =
    taxExecutorReason.trim().length >= 10 && taxExecutorReason.trim().length <= 200
  const onChain = status.data !== undefined && wallet.chainId === status.data.chainId
  const isOwner = onChain && sameAddress(wallet.address, status.data?.owner)

  const taxExecutorMutation = useMutation({
    mutationFn: async () => {
      if (taxExecutorPending) {
        try {
          return await api.post<TaxExecutorResult>(
            '/contracts/dividends/tax-executor-operations',
            taxExecutorPending,
          )
        } catch (error) {
          throw new TaxExecutorAuditPendingError(taxExecutorPending, error)
        }
      }

      if (
        !status.data ||
        !wallet.address ||
        !normalizedTaxExecutor ||
        !validTaxExecutor ||
        !validTaxExecutorReason
      ) {
        throw new Error('请先完成钱包、Tax executor 地址和操作理由校验。')
      }

      const txHash = await sendTaxExecutorOperation({
        taxExecutor: normalizedTaxExecutor,
        account: wallet.address,
        factory: status.data.factory,
      })
      const next = {
        taxExecutor: normalizedTaxExecutor,
        reason: taxExecutorReason.trim(),
        txHash,
      }
      setTaxExecutorPending(next)
      storeTaxExecutorPending(next)

      let receipt: Awaited<ReturnType<typeof waitForContractOperation>>
      try {
        receipt = await waitForContractOperation(txHash)
      } catch (error) {
        throw new SubmittedTaxExecutorError(next, error)
      }
      if (receipt.status !== 'success') throw new RevertedTransactionError('链上交易已回滚。')

      try {
        return await api.post<TaxExecutorResult>(
          '/contracts/dividends/tax-executor-operations',
          next,
        )
      } catch (error) {
        throw new TaxExecutorAuditPendingError(next, error)
      }
    },
    onSuccess: (result) => {
      setLastTaxExecutorResult(result)
      setTaxExecutorPending(null)
      storeTaxExecutorPending(null)
      setTaxExecutorReason('')
      void qc.invalidateQueries({ queryKey: ['contract-system-factory-status'] })
      toast.success('Tax executor 已更新并写入审计', {
        description: shortAddress(result.taxExecutor),
      })
    },
    onError: (error) => {
      if (error instanceof RevertedTransactionError) {
        setTaxExecutorPending(null)
        storeTaxExecutorPending(null)
        toast.error('链上交易已回滚', { description: 'Tax executor 没有变更，可以核对后重试。' })
        return
      }
      if (error instanceof SubmittedTaxExecutorError) {
        setTaxExecutorPending(error.pending)
        toast.warning('交易已提交，等待链上确认', {
          description: '请勿重复发送；稍后使用原交易补写审计。',
        })
        return
      }
      if (error instanceof TaxExecutorAuditPendingError) {
        if (error.terminal) {
          setTaxExecutorPending(null)
          storeTaxExecutorPending(null)
          toast.error('原交易未成功', { description: '已解除锁定，可以重新提交。' })
          return
        }
        setTaxExecutorPending(error.pending)
        toast.warning('交易已上链，但审计尚未完成', {
          description: `${error.message} 点击补写不会再次发送链上交易。`,
        })
        return
      }
      toast.error('修改失败', { description: contractWalletErrorMessage(error) })
    },
  })

  const operationBusy = Boolean(taxExecutorPending) || taxExecutorMutation.isPending

  return (
    <Page>
      <PageHeader
        title="系统金库配置"
        description="管理 SystemFactory 的税转换执行地址。分红释放周期由每次发射签名锁定，不再是 Factory 可修改的全局配置。"
        actions={
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            disabled={status.isFetching}
            onClick={() => void status.refetch()}
          >
            <RefreshCw className={cn('size-3.5', status.isFetching && 'animate-spin')} />
            刷新状态
          </Button>
        }
      />

      {canManage && (
        <ContractWalletBar
          status={status.data}
          isLoading={status.isLoading}
          isError={status.isError}
          error={status.error}
          onRetry={() => void status.refetch()}
          wallet={wallet}
          walletBusy={walletBusy}
          expected={[{ label: 'SystemFactory owner', address: status.data?.owner ?? null }]}
          onConnect={connect}
          onDisconnect={disconnect}
          onSwitchChain={switchChain}
        />
      )}

      {taxExecutorPending && (
        <Alert className="surface border-warn/30 bg-warn/[0.04]">
          <AlertTriangle className="text-warn" />
          <AlertTitle>Tax executor 交易待补写审计</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {shortAddress(taxExecutorPending.taxExecutor)} · 交易{' '}
              <a href={explorerTxUrl(taxExecutorPending.txHash)} target="_blank" rel="noreferrer">
                {shortAddress(taxExecutorPending.txHash)}
              </a>
            </span>
            <Button
              size="sm"
              disabled={taxExecutorMutation.isPending}
              onClick={() => taxExecutorMutation.mutate()}
            >
              {taxExecutorMutation.isPending ? '核验中…' : '用原交易补写审计'}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {lastTaxExecutorResult && !taxExecutorPending && (
        <a
          href={explorerTxUrl(lastTaxExecutorResult.txHash)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 rounded-apple bg-green/[0.07] px-3.5 py-2.5 text-[11.5px] text-green"
        >
          <CheckCircle2 className="size-3.5" />
          Tax executor 已更新：{shortAddress(lastTaxExecutorResult.taxExecutor)} ·{' '}
          {shortAddress(lastTaxExecutorResult.txHash)}
          <ExternalLink className="size-3" />
        </a>
      )}

      {status.isLoading ? (
        <Skeleton className="h-72 w-full rounded-apple-lg" />
      ) : status.isError || !status.data ? (
        <Alert variant="destructive" className="surface">
          <AlertTriangle />
          <AlertTitle>无法读取链上配置</AlertTitle>
          <AlertDescription>
            {status.error instanceof ApiError ? status.error.message : '请检查后台和链上 RPC。'}
          </AlertDescription>
        </Alert>
      ) : (
        <section className="surface space-y-6 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="surface-inset flex size-10 items-center justify-center">
                <KeyRound className="size-4 text-cyan" />
              </div>
              <div>
                <h2 className="text-[15px] font-medium text-txt">税转换执行地址</h2>
                <p className="mt-1 text-[12.5px] text-mut2">
                  当前值：{' '}
                  <a
                    href={explorerAddressUrl(status.data.taxExecutor)}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono font-medium text-cyan"
                  >
                    {shortAddress(status.data.taxExecutor)}
                  </a>
                </p>
              </div>
            </div>
            <div className="text-right font-mono text-[11px] text-mut2">
              <a href={explorerAddressUrl(status.data.factory)} target="_blank" rel="noreferrer">
                Factory {shortAddress(status.data.factory)}
              </a>
              <div>Owner {shortAddress(status.data.owner)}</div>
            </div>
          </div>

          {canManage && (
            <div className="surface-inset space-y-4 p-4">
              <div className="space-y-2">
                <Label htmlFor="tax-executor">新 Tax executor 地址</Label>
                <Input
                  id="tax-executor"
                  className="font-mono"
                  value={taxExecutorInput}
                  disabled={operationBusy}
                  aria-invalid={taxExecutorInput !== '' && !validTaxExecutor}
                  placeholder="0x…"
                  onChange={(event) => setTaxExecutorInput(event.target.value)}
                />
                <p className="text-[11.5px] text-mut2">
                  必须是非零 EVM 地址。新地址生效后，旧 keeper 将不能再触发当前工厂金库的税转换。
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax-executor-reason">操作理由</Label>
                <Textarea
                  id="tax-executor-reason"
                  value={taxExecutorReason}
                  minLength={10}
                  maxLength={200}
                  disabled={operationBusy}
                  placeholder="至少 10 个字符，将写入不可修改的审计记录"
                  onChange={(event) => setTaxExecutorReason(event.target.value)}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  disabled={
                    operationBusy || !taxExecutorChanged || !validTaxExecutorReason || !isOwner
                  }
                  onClick={() => taxExecutorMutation.mutate()}
                >
                  {taxExecutorMutation.isPending ? '等待钱包与链上确认…' : '轮换 Tax executor'}
                </Button>
              </div>
            </div>
          )}
        </section>
      )}

      <Alert className="surface-inset border-warn/25 bg-warn/[0.04]">
        <AlertTriangle className="text-warn" />
        <AlertTitle>Tax executor 立即生效</AlertTitle>
        <AlertDescription>
          新地址会立即作用于当前 SystemFactory 创建的现有和未来 System Vault。由退休工厂创建的 Vault
          仍读取各自旧工厂的 executor，需要在对应旧工厂单独轮换。
        </AlertDescription>
      </Alert>
    </Page>
  )
}
